import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useEffect, useRef, useState } from "react";

export function useField(
  elementRef: React.RefObject<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >,
  /** Optional value to watch - when it changes, recheck validation (useful for hidden inputs) */
  value?: unknown,
) {
  const [showErrors, setShowErrors] = useState(false);
  const [forceOpenTooltip, setForceOpenTooltip] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>();
  const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleSubmit = () => {
      if (hideTooltipTimeoutRef.current) {
        clearTimeout(hideTooltipTimeoutRef.current);
      }

      setShowErrors(true);
      setForceOpenTooltip(true);
      checkValidationMessage();

      hideTooltipTimeoutRef.current = setTimeout(() => {
        setForceOpenTooltip(false);
      }, 3000);
    };

    const checkValidationMessage = () => {
      // This `setTimeout` is ugly, but necessary, because we're using native listeners, yet we change React state based on that.
      // Without the timeout it interferes with typing.
      setTimeout(() => {
        setValidationMessage(getValidationMessage(element));
      });
    };

    const form = element.form;
    form?.addEventListener("submit", handleSubmit);
    element.addEventListener("input", checkValidationMessage);
    element.addEventListener("blur", checkValidationMessage);

    return () => {
      form?.removeEventListener("submit", handleSubmit);
      element.removeEventListener("input", checkValidationMessage);
      element.removeEventListener("blur", checkValidationMessage);
    };
  }, [elementRef]);

  // Recheck validation when watched value changes (for hidden inputs that don't fire input events)
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !showErrors) return;
    setValidationMessage(getValidationMessage(element));
  }, [value, showErrors, elementRef]);

  const isInvalid = showErrors && !!validationMessage;

  return {
    showErrors,
    isInvalid,
    tooltip: {
      forceOpen: forceOpenTooltip,
      message: showErrors ? validationMessage : undefined,
    },
  };
}

function getValidationMessage(
  element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
) {
  if (
    element.validity.typeMismatch &&
    element instanceof HTMLInputElement &&
    element.type === "email"
  ) {
    return i18n._(msg`Please enter a valid email address.`);
  }
  return element.validationMessage;
}

export interface TooltipModel {
  forceOpen: boolean;
  message?: string;
}

export interface FieldProps<M extends object, V> {
  model?: M;
  attr?: keyof M;
  value?: V;
  onChange?: (newValue: V) => void;
}

/**
 * Resolves `model`/`attr` and `value`/`onChange` props into a single `{ value, onChange }` pair.
 * If `value` is provided, it takes precedence over `model[attr]`.
 * `onChange` writes to `model[attr]` and calls the provided `onChange` callback.
 */
export function useModelBinding<M extends object, V>({
  model,
  attr,
  value: propsValue,
  onChange: propsOnChange,
}: {
  model?: M;
  attr?: keyof M;
  value?: V;
  onChange?: (newValue: V) => void;
}) {
  const value =
    propsValue !== undefined
      ? propsValue
      : model && attr
        ? (model[attr] as V)
        : undefined;

  const onChange = (newValue: V) => {
    if (model && attr) {
      model[attr] = newValue as any;
    }
    propsOnChange?.(newValue);
  };

  return { value, onChange };
}

/** A display label that is either a plain string or a lazy translation. */
export type EnumLabel = string | MessageDescriptor;

/** `values` accepted by enum-backed fields: a label map or a bare value list. */
export type EnumValues<T extends string | number = string> =
  | T[]
  | Record<string, EnumLabel>;

/** Resolves a label to a string for the active locale. */
export function resolveEnumLabel(label: EnumLabel | undefined): string {
  if (label == null) return "";
  return typeof label === "string" ? label : i18n._(label);
}

/**
 * Label for `value`, falling back to the value itself when `values` has no
 * entry for it. Backends do send values outside the map the frontend declares
 * (e.g. a PPG `measurement_condition` of "rest"), and `resolveEnumLabel` alone
 * returns "" for those, which would hide what the row actually holds.
 *
 * Nullish is the exception: it resolves to "" for the caller's own placeholder,
 * since the pass-through would otherwise render the text "undefined"/"null".
 */
export function resolveEnumValueLabel<V extends string | number>(
  values: EnumValues<V>,
  value: V | null | undefined,
): string {
  if (value == null) return "";
  if (Array.isArray(values)) return String(value);
  // Numeric enums key the record by the number's string form, so `String`
  // covers both those and plain string-keyed maps.
  const label = values[String(value)];
  return label === undefined ? String(value) : resolveEnumLabel(label);
}

export function valuesToMap<T extends string | number>(values: EnumValues<T>) {
  const res = new Map<T, string>();

  if (Array.isArray(values)) {
    for (const item of values) {
      res.set(item, String(item));
    }
  } else {
    const allKeysAreNumbers = Object.keys(values).every(
      (v) => !isNaN(v as any),
    );

    for (const key of Object.keys(values)) {
      res.set(
        (allKeysAreNumbers ? parseInt(key) : key) as T,
        resolveEnumLabel(values[key]),
      );
    }
  }

  return res;
}
